<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="isLoading"
    :disabled="isLoading"
    loading-message="Loading a Grocery Data..."
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Get Grocery Estimation and Cost
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Plan</label>
        <div class="col-sm-8">
          <dropdown-list
            v-model="groceryInfo.plan"
            :options="planList"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Phone Number</label>
        <phone-number-input
          id="customer-phone"
          v-model="groceryInfo.phoneNumber"
          class="col-sm-6"
          :page-id="pageId"
          required
          disabled
        />
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">IC Total*</label>
        <div class="col-sm-7">
          <input
            v-model="groceryInfo.serviceFee"
            class="form-control input-no-border"
            min="1"
            type="number"
            step="0.01"
            required
            autocomplete="nope"
          >
        </div>
      </div>

      <div class="row">
        <label
          class="col-sm-3 control-label"
          style="padding-top: 15px"
        >Tip</label>
        <div class="col-sm-5">
          <el-input
            v-if="isCashTip"
            :id="`tip-${pageId}`"
            v-model="tipField"
            title="Click $-symbol to switch to percentage tip"
            min="0"
            max="10000"
            type="number"
            step="0.1"
            placeholder="5"
            size="large"
          >
            <template #prepend>
              <div
                style="cursor: pointer"
                @click="switchTipContext"
              >
                $
              </div>
            </template>
          </el-input>
          <el-input
            v-else
            :id="`tip-${pageId}`"
            v-model="tipField"
            title="Click %-symbol to switch to dollar tip"
            min="0"
            max="100"
            type="number"
            step="1"
            placeholder="5"
            size="large"
          >
            <template #prepend>
              <div
                style="cursor: pointer"
                @click="switchTipContext"
              >
                %
              </div>
            </template>
          </el-input>
          <span class="help-block"> Click {{ isCashTip ? '$' : '%' }} to switch to {{ isCashTip ? '%' : '$' }} </span>
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Number of Items*</label>
        <div class="col-sm-7">
          <input
            v-model="groceryInfo.numberOfItems"
            min="1"
            type="number"
            step="1"
            required
            class="form-control"
            autocomplete="nope"
          >
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-8 offset-3">
          <el-checkbox
            id="doNotCharge"
            v-model="groceryInfo.doNotCharge"
          >
            Do not charge Grocery
          </el-checkbox>
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-8 offset-3">
          <el-checkbox
            id="noGoGoFee"
            v-model="groceryInfo.noGoGoFee"
          >
            Do not charge GoGo Fee
          </el-checkbox>
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-8 offset-3">
          <el-checkbox
            id="applyDiscountedFee"
            v-model="groceryInfo.applyDiscountedFee"
          >
            Apply discounted GoGo Fee
          </el-checkbox>
        </div>
      </div>

      <template v-if="costInfo">
        <hr>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">IC Total:</label>
          <div class="col-sm-6">
            ${{ costInfo.serviceFee }}
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Concierge Fee:</label>
          <div class="col-sm-6">
            ${{ costInfo.gogoFee }}
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Expiring Credit:</label>
          <div
            v-if="costInfo.expiringCredit > 0"
            class="col-sm-6 ecAttention"
          >
            ${{ costInfo.expiringCredit }}
          </div>
          <div
            v-else
            class="col-sm-6"
          >
            No
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Tip:</label>
          <div class="col-sm-6">
            ${{ tipAmount }}
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-sm-4 control-label">Total Cost:</label>
          <div
            v-if="costInfo.expiringCredit > 0"
            class="col-sm-6"
          >
            <s>${{ totalAmount }}</s> <span style="color: green">${{ totalAmountWithEc }}</span>
          </div>
          <div
            v-else
            class="col-sm-6"
          >
            ${{ totalAmount }}
          </div>
        </div>
      </template>
    </template>

    <template #submit-button>
      Get Estimation Cost
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

import util from '@/Utils/util';
import { PlanName } from '@/gql/graphql';
import { useGlobal } from '@/use/global';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { defaultGroceryTipPercentage } from '@/constants/app';
import { GroceryCost } from '@/models/grocery';
import { groceryService } from '@/services/grocery';
import _ from 'lodash';

const pageId = 'get-grocery-estimation-and-cost';

const { customerPhoneNumber, customerActivePlan } = useCustomer();
const { showErrorPopupDialog } = useSwalDialog();
const { lastFormData, setFormData } = useGlobal(pageId);
const customerPlanName = computed(() => customerActivePlan.value?.planName as PlanName)

const planList = computed(() => {
  const shouldShowCustomerPlan = customerPlanName.value && ![PlanName.Basic15, PlanName.Value30].includes(customerPlanName.value)
  return [
    { id: 1, key: PlanName.Basic15, label: `Basic${customerPlanName.value === PlanName.Basic15 ? ' - Current Plan' : ''}` },
    { id: 2, key: PlanName.Value30, label: `Value${customerPlanName.value === PlanName.Value30 ? ' - Current Plan' : ''}` },
    ...(shouldShowCustomerPlan ? [{ id: 3, key: customerPlanName.value, label: `${_.capitalize(customerPlanName.value)} - Current Plan` }] : [])
  ]
});

const groceryInfo = ref({
  plan: customerPlanName.value ?? planList.value[0].key,
  phoneNumber: '',
  serviceFee: 0,
  numberOfItems: 0,
  noGoGoFee: false,
  doNotCharge: false,
  applyDiscountedFee: false,
});

const costInfo = ref<GroceryCost | null>(null);
const tipField = ref(0);
const isCashTip = ref(false);
const isLoading = ref(false);

const tipAmount = computed(() =>
  isCashTip.value ? Number(tipField.value) : (Number(costInfo.value?.serviceFee ?? 0) * Number(tipField.value)) / 100,
);
const totalAmount = computed(() => (costInfo.value ? (Number(costInfo.value.total) + Number(tipAmount.value)).toFixed(2) : 0));
const totalAmountWithEc = computed(() => {
  const ec = costInfo.value?.expiringCredit ?? 0;
  if (ec > 0) {
    return Math.max(Number(totalAmount.value) - ec, 0).toFixed(2);
  }

  return totalAmount.value;
});

const resetDefaultTip = () => {
  tipField.value = isCashTip.value ? 0 : defaultGroceryTipPercentage;
};

const switchTipContext = () => {
  isCashTip.value = !isCashTip.value;
  resetDefaultTip();
};

const submitForm = async () => {
  isLoading.value = true;
  try {
    costInfo.value = await groceryService.getGroceryEstimation({
      planName: groceryInfo.value.plan,
      phoneNumber: groceryInfo.value.phoneNumber,
      serviceFee: Number(groceryInfo.value.serviceFee),
      numberOfItems: Number(groceryInfo.value.numberOfItems),
      noGoGoFee: groceryInfo.value.noGoGoFee,
      doNotCharge: groceryInfo.value.doNotCharge,
      applyDiscountedFee: groceryInfo.value.applyDiscountedFee,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};

const resetForm = () => {
  groceryInfo.value = {
    plan: customerPlanName.value ?? planList.value[0].key,
    phoneNumber: customerPhoneNumber.value || '',
    serviceFee: 0,
    numberOfItems: 0,
    noGoGoFee: false,
    doNotCharge: false,
    applyDiscountedFee: false,
  };
  costInfo.value = null;
  resetDefaultTip();
};

watch(
  () => groceryInfo.value.doNotCharge,
  (value) => {
    if (value) {
      groceryInfo.value.noGoGoFee = false;
      groceryInfo.value.applyDiscountedFee = false;
    }
  },
);

watch(
  () => groceryInfo.value.noGoGoFee,
  (value) => {
    if (value) {
      groceryInfo.value.doNotCharge = false;
      groceryInfo.value.applyDiscountedFee = false;
    }
  },
);

watch(
  () => groceryInfo.value.applyDiscountedFee,
  (value) => {
    if (value) {
      groceryInfo.value.doNotCharge = false;
      groceryInfo.value.noGoGoFee = false;
    }
  },
);

watch(customerPlanName, () => {
  groceryInfo.value.plan = customerPlanName.value ?? planList.value[0].key;
})

onMounted(() => {
  resetDefaultTip();
  groceryInfo.value.phoneNumber = customerPhoneNumber.value || lastFormData.value?.phoneNumber || '';
});

watch(customerPhoneNumber, resetForm);

onBeforeUnmount(() => {
  setFormData(groceryInfo.value);
});
</script>

<style lang="scss">
.get-grocery-estimation {
  input[type='number'] {
    color: #000;
  }

  .el-input.el-input-group--prepend {
    width: 120px;
  }
}

.ecAttention {
  border: 1px solid #f9e24a;
  border-radius: 0.3em;
  background-color: #fcf9c2;
  color: #824e0e;
  padding: 0.5em;
  font-weight: bold;
  flex-basis: content;
}
</style>
